import './entity-statistics-dashboard.scss';
import {Box, Button} from '@mui/material';
import {useCallback, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {translations} from '../../i18n/translations';
import {TextChart} from '../charts/text-chart/text-chart';
import {TableChart, TableChartRows} from '../charts/table-chart/table-chart';
import {PageHeader} from '../page-header/page-header';
import {AreaChart, AreaChartData} from '../charts/area-chart/area-chart';
import {BarChart, BarChartData} from '../charts/bar-chart/bar-chart';
import {PieChart, PieChartData} from '../charts/pie-chart/pie-chart';
import {StackChart, StackChartData} from '../charts/stack-chart/stack-chart';
import {StatisticsRepository} from '../../repositories/statistics';
import {CallDirection} from '../../backend/enums/call-direction';
import {chartColors} from '../../utils/constants/colors';
import {StatisticsBinning} from '../../backend/enums/statistics-binning';
import {getStatisticsBinningByDates} from '../../utils/functions/get-statistics-binning';
import {getXLabelStatisticsDashboardChart} from '../../utils/functions/get-x-label-statistics-dashboard-chart';
import RefreshIcon from '@mui/icons-material/Refresh';
import {Moment} from 'moment';
import {DeviceWorkgroup} from '../../backend/enums/device-workgroup';
import {ActiveDevicesCountEntitiesStatistics} from '../../backend/responses/statistics/entities/active-devices-count';

enum NumberDevicesColumnIdentifier {
  DeviceType = 'device-type',
  Total = 'total',
}

enum TopUsersColumnIdentifier {
  User = 'user',
  Total = 'total',
}

type EntityStatisticsDashboardProps = {
  entityId: string;
  startDate: Moment;
  endDate: Moment;
  showTitle?: boolean;
  notShowRefreshButton?: boolean;
};

/**
 * Entity statistics dashboard.
 */
export const EntityStatisticsDashboard = (props: EntityStatisticsDashboardProps) => {
  // Hold state
  const [numberActiveSeniors, setNumberActiveSeniors] = useState<number | undefined>();
  const [loadingNumberActiveSeniors, setLoadingNumberActiveSeniors] = useState<boolean>(true);
  const [errorNumberActiveSeniors, setErrorNumberActiveSeniors] = useState<string | undefined>();
  const [numberActiveProfessionalCaregivers, setNumberActiveProfessionalCaregivers] = useState<
    number | undefined
  >();
  const [loadingNumberActiveProfessionalCaregivers, setLoadingNumberActiveProfessionalCaregivers] =
    useState<boolean>(true);
  const [errorNumberActiveProfessionalCaregivers, setErrorNumberActiveProfessionalCaregivers] =
    useState<string | undefined>();
  const [numberActiveInformalCaregivers, setNumberActiveInformalCaregivers] = useState<
    number | undefined
  >();
  const [loadingNumberActiveInformalCaregivers, setLoadingNumberActiveInformalCaregivers] =
    useState<boolean>(true);
  const [errorNumberActiveInformalCaregivers, setErrorNumberActiveInformalCaregivers] = useState<
    string | undefined
  >();
  const [numberPendingInvitations, setNumberPendingInvitations] = useState<number | undefined>();
  const [loadingNumberPendingInvitations, setLoadingNumberPendingInvitations] =
    useState<boolean>(true);
  const [errorNumberPendingInvitations, setErrorNumberPendingInvitations] = useState<
    string | undefined
  >();
  const [numberCalls, setNumberCalls] = useState<number | undefined>();
  const [loadingNumberCalls, setLoadingNumberCalls] = useState<boolean>(true);
  const [errorNumberCallsLast, setErrorNumberCalls] = useState<string | undefined>();
  const [numberIncomingCalls, setNumberIncomingCalls] = useState<number | undefined>();
  const [loadingNumberIncomingCalls, setLoadingNumberIncomingCalls] = useState<boolean>(true);
  const [errorNumberIncomingCalls, setErrorNumberIncomingCalls] = useState<string | undefined>();
  const [numberOutgoingCalls, setNumberOutgoingCalls] = useState<number | undefined>();
  const [loadingNumberOutgoingCalls, setLoadingNumberOutgoingCalls] = useState<boolean>(true);
  const [errorNumberOutgoingCalls, setErrorNumberOutgoingCalls] = useState<string | undefined>();
  const [numberActiveDigitalInclusionDevices, setNumberActiveDigitalInclusionDevices] =
    useState<TableChartRows>([]);
  const [
    loadingNumberActiveDigitalInclusionDevices,
    setLoadingNumberActiveDigitalInclusionDevices,
  ] = useState<boolean>(true);
  const [errorNumberActiveDigitalInclusionDevices, setErrorNumberActiveDigitalInclusionDevices] =
    useState<string | undefined>();
  const [numberActiveQuickAccessDevices, setNumberActiveQuickAccessDevices] =
    useState<TableChartRows>([]);
  const [loadingNumberActiveQuickAccessDevices, setLoadingNumberActiveQuickAccessDevices] =
    useState<boolean>(true);
  const [errorNumberActiveQuickAccessDevices, setErrorNumberActiveQuickAccessDevices] = useState<
    string | undefined
  >();
  const [topUsers, setTopUsers] = useState<TableChartRows>([]);
  const [loadingTopUsers, setLoadingTopUsers] = useState<boolean>(true);
  const [errorTopUsers, setErrorTopUsers] = useState<string | undefined>();
  const [generalSystemUsage, setGeneralSystemUsage] = useState<AreaChartData>([]);
  const [loadingGeneralSystemUsage, setLoadingGeneralSystemUsage] = useState<boolean>(true);
  const [errorGeneralSystemUsage, setErrorGeneralSystemUsage] = useState<string | undefined>();
  const [callsEvolution, setCallsEvolution] = useState<BarChartData>([]);
  const [loadingCallsEvolution, setLoadingCallsEvolution] = useState<boolean>(true);
  const [errorCallsEvolution, setErrorCallsEvolution] = useState<string | undefined>();
  const [mostUsedApplications, setMostUsedApplications] = useState<PieChartData>([]);
  const [loadingMostUsedApplications, setLoadingMostUsedApplications] = useState<boolean>(true);
  const [errorMostUsedApplications, setErrorMostUsedApplications] = useState<string | undefined>();
  const [applicationsUsage, setApplicationsUsage] = useState<StackChartData>({
    data: [],
    coordinatesData: [],
  });
  const [loadingApplicationsUsage, setLoadingApplicationsUsage] = useState<boolean>(true);
  const [errorApplicationsUsage, setErrorApplicationsUsage] = useState<string | undefined>();

  // Translations
  const {t} = useTranslation();

  // Get the number of active seniors
  const getNumberActiveSeniors = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberActiveSeniors(true);

      // Clear error
      setErrorNumberActiveSeniors(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getActiveSeniorsCountInEntity(
        props.entityId
      );

      // Set value
      setNumberActiveSeniors(result.activeSeniorsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of active seniors for entity ${props.entityId}.`,
        error
      );

      // Set value
      setNumberActiveSeniors(undefined);

      // Set error
      setErrorNumberActiveSeniors(error.message);
    } finally {
      // Stop loading
      setLoadingNumberActiveSeniors(false);
    }
  }, [props.entityId]);

  // Get the number of active professional caregivers
  const getNumberActiveProfessionalCaregivers = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberActiveProfessionalCaregivers(true);

      // Clear error
      setErrorNumberActiveProfessionalCaregivers(undefined);

      // Make request to the backend
      const result =
        await StatisticsRepository.instance.getActiveProfessionalCaregiversCountInEntity(
          props.entityId
        );

      // Set value
      setNumberActiveProfessionalCaregivers(result.activeProfessionalCaregiversCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of active professional caregivers for entity ${props.entityId}.`,
        error
      );

      // Set value
      setNumberActiveProfessionalCaregivers(undefined);

      // Set error
      setErrorNumberActiveProfessionalCaregivers(error.message);
    } finally {
      // Stop loading
      setLoadingNumberActiveProfessionalCaregivers(false);
    }
  }, [props.entityId]);

  // Get the number of active informal caregivers
  const getNumberActiveInformalCaregivers = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberActiveInformalCaregivers(true);

      // Clear error
      setErrorNumberActiveInformalCaregivers(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getActiveInformalCaregiversCountInEntity(
        props.entityId
      );

      // Set value
      setNumberActiveInformalCaregivers(result.activeInformalCaregiversCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of active informal caregivers for entity ${props.entityId}.`,
        error
      );

      // Set value
      setNumberActiveInformalCaregivers(undefined);

      // Set error
      setErrorNumberActiveInformalCaregivers(error.message);
    } finally {
      // Stop loading
      setLoadingNumberActiveInformalCaregivers(false);
    }
  }, [props.entityId]);

  // Get the number of pending invitations
  const getNumberPendingInvitations = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberPendingInvitations(true);

      // Clear error
      setErrorNumberPendingInvitations(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getInProgressInvitesCountInEntity(
        props.entityId
      );

      // Set value
      setNumberPendingInvitations(result.inProgressInvitesCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of pending invitations for entity ${props.entityId}.`,
        error
      );

      // Set value
      setNumberPendingInvitations(undefined);

      // Set error
      setErrorNumberPendingInvitations(error.message);
    } finally {
      // Stop loading
      setLoadingNumberPendingInvitations(false);
    }
  }, [props.entityId]);

  // Get the number of calls: last X days
  const getNumberCalls = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberCalls(true);

      // Clear error
      setErrorNumberCalls(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsCountInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString())
      );

      // Set value
      setNumberCalls(result.callsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of calls for entity ${props.entityId} in the last 30 days.`,
        error
      );

      // Set value
      setNumberCalls(undefined);

      // Set error
      setErrorNumberCalls(error.message);
    } finally {
      // Stop loading
      setLoadingNumberCalls(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get the number of incoming calls: last X days
  const getNumberIncomingCalls = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberIncomingCalls(true);

      // Clear error
      setErrorNumberIncomingCalls(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsCountInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        CallDirection.CaregiverToSenior
      );

      // Set value
      setNumberIncomingCalls(result.callsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of incoming calls for entity ${props.entityId} in the last 30 days.`,
        error
      );

      // Set value
      setNumberIncomingCalls(undefined);

      // Set error
      setErrorNumberIncomingCalls(error.message);
    } finally {
      // Stop loading
      setLoadingNumberIncomingCalls(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get the number of outgoing calls: last X days
  const getNumberOutgoingCalls = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberOutgoingCalls(true);

      // Clear error
      setErrorNumberOutgoingCalls(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsCountInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        CallDirection.SeniorToCaregiver
      );

      // Set value
      setNumberOutgoingCalls(result.callsCount);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of outgoing calls for entity ${props.entityId} in the last 30 days.`,
        error
      );

      // Set value
      setNumberOutgoingCalls(undefined);

      // Set error
      setErrorNumberOutgoingCalls(error.message);
    } finally {
      // Stop loading
      setLoadingNumberOutgoingCalls(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  const processActiveDevicesResult = useCallback(
    (data: ActiveDevicesCountEntitiesStatistics['data']): TableChartRows => {
      return data.map((obj) => {
        return {
          id: obj.deviceType,
          columns: [
            {
              columnId: NumberDevicesColumnIdentifier.DeviceType,
              value: t(translations.device_types[obj.deviceType]),
            },
            {columnId: NumberDevicesColumnIdentifier.Total, value: obj.activeCount},
          ],
        };
      });
    },
    [t]
  );

  // Get the number of active digital inclusion devices
  const getNumberActiveDigitalInclusionDevices = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberActiveDigitalInclusionDevices(true);

      // Clear error
      setErrorNumberActiveDigitalInclusionDevices(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getActiveDevicesCountInEntity(
        props.entityId,
        DeviceWorkgroup.DigitalInclusion
      );

      // Set value
      setNumberActiveDigitalInclusionDevices(processActiveDevicesResult(result.data));
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of active digital inclusion devices for entity ${props.entityId}.`,
        error
      );

      // Set value
      setNumberActiveDigitalInclusionDevices([]);

      // Set error
      setErrorNumberActiveDigitalInclusionDevices(error.message);
    } finally {
      // Stop loading
      setLoadingNumberActiveDigitalInclusionDevices(false);
    }
  }, [processActiveDevicesResult, props.entityId]);

  // Get the number of active quick access devices
  const getNumberActiveQuickAccessDevices = useCallback(async () => {
    try {
      // Start loading
      setLoadingNumberActiveQuickAccessDevices(true);

      // Clear error
      setErrorNumberActiveQuickAccessDevices(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getActiveDevicesCountInEntity(
        props.entityId,
        DeviceWorkgroup.QuickAccess
      );

      // Set value
      setNumberActiveQuickAccessDevices(processActiveDevicesResult(result.data));
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the number of active quick access devices for entity ${props.entityId}.`,
        error
      );

      // Set value
      setNumberActiveQuickAccessDevices([]);

      // Set error
      setErrorNumberActiveQuickAccessDevices(error.message);
    } finally {
      // Stop loading
      setLoadingNumberActiveQuickAccessDevices(false);
    }
  }, [processActiveDevicesResult, props.entityId]);

  // Get the top users: last X days
  const getTopUsers = useCallback(async () => {
    try {
      // Start loading
      setLoadingTopUsers(true);

      // Clear error
      setErrorTopUsers(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getTopUsersInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString())
      );

      // Process
      const processedResult: TableChartRows = result.data.map((obj) => {
        return {
          id: obj.user.id,
          columns: [
            {
              columnId: TopUsersColumnIdentifier.User,
              value: obj.user.name,
              avatarUrl: obj.user.picture,
            },
            {columnId: TopUsersColumnIdentifier.Total, value: obj.usageTimeInHours},
          ],
        };
      });

      // Set value
      setTopUsers(processedResult);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the top users for entity ${props.entityId} in the last 30 days.`,
        error
      );

      // Set value
      setTopUsers([]);

      // Set error
      setErrorTopUsers(error.message);
    } finally {
      // Stop loading
      setLoadingTopUsers(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get the general system usage
  const getGeneralSystemUsage = useCallback(async () => {
    try {
      // Start loading
      setLoadingGeneralSystemUsage(true);

      // Clear error
      setErrorGeneralSystemUsage(undefined);

      // Update time lapse
      setGeneralSystemUsage([]);

      // Get the binning
      const binning: StatisticsBinning = getStatisticsBinningByDates(
        props.startDate,
        props.endDate
      );

      // Make request to the backend
      const result = await StatisticsRepository.instance.getSystemUsageInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        binning
      );

      // Process
      const processedResult: AreaChartData = result.data.map((obj) => {
        return {
          y: obj.durationInHours,
          x: getXLabelStatisticsDashboardChart(obj.month - 1, obj.year, result.binning, obj.date),
        };
      });

      // Set value
      setGeneralSystemUsage(processedResult);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the general system usage for entity ${props.entityId}.`,
        error
      );

      // Set error
      setErrorGeneralSystemUsage(error.message);
    } finally {
      // Stop loading
      setLoadingGeneralSystemUsage(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get the calls evolution
  const getCallsEvolution = useCallback(async () => {
    try {
      // Start loading
      setLoadingCallsEvolution(true);

      // Clear error
      setErrorCallsEvolution(undefined);

      // Update time lapse
      setCallsEvolution([]);

      // Get the binning
      const binning = getStatisticsBinningByDates(props.startDate, props.endDate);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getCallsEvolutionInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        binning
      );

      // Process
      const processedResult: BarChartData = result.data.map((obj) => {
        return {
          y: obj.callsCount,
          x: getXLabelStatisticsDashboardChart(obj.month - 1, obj.year, result.binning, obj.date),
        };
      });

      // Set value
      setCallsEvolution(processedResult);
    } catch (error: any) {
      // Log
      console.debug(`🔴 Could not get the calls evolution for entity ${props.entityId}.`, error);

      // Set error
      setErrorCallsEvolution(error.message);
    } finally {
      // Stop loading
      setLoadingCallsEvolution(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get the most used applications: last X days
  const getMostUsedApplications = useCallback(async () => {
    try {
      // Start loading
      setLoadingMostUsedApplications(true);

      // Clear error
      setErrorMostUsedApplications(undefined);

      // Make request to the backend
      const result = await StatisticsRepository.instance.getMostUsedApplicationsInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        true
      );

      // Process
      let processedResult: PieChartData = [];
      let colorIndex: number = 0;
      for (let i = 0; i < result.data.length; i++) {
        // Get element
        const element = result.data[i];

        // Color to used
        let chartColor: string = '';
        if (colorIndex < chartColors.length) {
          // Set color
          chartColor = chartColors[colorIndex];

          // Increment
          colorIndex += 1;
        } else {
          // Set color
          chartColor = chartColors[0];

          // Reset
          colorIndex = 1;
        }

        // Add to processed result
        processedResult.push({
          id: element.application.id || '',
          y: element.usageTimeInHours,
          x: element.application.name,
          color: chartColor,
        });
      }

      // Set value
      setMostUsedApplications(processedResult);
    } catch (error: any) {
      // Log
      console.debug(
        `🔴 Could not get the most used applications for entity ${props.entityId} in the last 30 days.`,
        error
      );

      // Set value
      setMostUsedApplications([]);

      // Set error
      setErrorMostUsedApplications(error.message);
    } finally {
      // Stop loading
      setLoadingMostUsedApplications(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get the applications usage
  const getApplicationsUsage = useCallback(async () => {
    try {
      // Start loading
      setLoadingApplicationsUsage(true);

      // Cleat error
      setErrorApplicationsUsage(undefined);

      // Update time lapse
      setApplicationsUsage({
        data: [],
        coordinatesData: [],
      });

      // Get the binning
      const binning: StatisticsBinning = getStatisticsBinningByDates(
        props.startDate,
        props.endDate
      );

      // Make request to the backend
      const result = await StatisticsRepository.instance.getSystemUsagePerApplicationInEntity(
        props.entityId,
        encodeURIComponent(props.startDate.toISOString()),
        encodeURIComponent(props.endDate.toISOString()),
        true,
        binning
      );

      // Get the x values
      let xValues: Array<string> = [];
      for (let i = 0; i < result.data.length; i++) {
        // Value x
        const xValue: string = getXLabelStatisticsDashboardChart(
          result.data[i].month - 1,
          result.data[i].year,
          result.binning,
          result.data[i]?.date
        );

        // Add x value
        if (!xValues.find((label) => label === xValue)) {
          xValues.push(xValue);
        }
      }

      // Process
      let processedLabels: StackChartData['coordinatesData'] = [];
      let processedData: StackChartData['data'] = [];
      let colorIndex: number = 0;
      for (let i = 0; i < result.data.length; i++) {
        // Get element
        const element = result.data[i];

        // Add to processed result
        if (
          element.application.id &&
          !processedData.find((obj) => obj.id === element.application.id)
        ) {
          // Color to use
          let chartColor: string = '';
          if (colorIndex < chartColors.length) {
            // Set color
            chartColor = chartColors[colorIndex];

            // Increment
            colorIndex += 1;
          } else {
            // Set color
            chartColor = chartColors[0];

            // Reset
            colorIndex = 1;
          }

          // Data
          processedData.push({
            id: element.application.id,
            label: element.application.name,
            color: chartColor,
            values: xValues.map((label) => {
              // Get the y value
              const yValue: number =
                result.data.find(
                  (obj) =>
                    obj.application.id === element.application.id &&
                    getXLabelStatisticsDashboardChart(
                      obj.month - 1,
                      obj.year,
                      result.binning,
                      obj?.date
                    ) === label
                )?.usageTimeInHours || 0;

              // Process labels
              let coordinateIndex = processedLabels.findIndex(
                (obj) => obj.x === label && obj.y === yValue
              );
              if (coordinateIndex !== -1) {
                processedLabels[coordinateIndex] = {
                  ...processedLabels[coordinateIndex],
                  stacks: [
                    ...processedLabels[coordinateIndex].stacks,
                    {
                      stackId: element.application.id as string,
                      stackLabel: element.application.name,
                    },
                  ],
                };
              } else {
                processedLabels.push({
                  x: label,
                  y: yValue,
                  stacks: [
                    {
                      stackId: element.application.id as string,
                      stackLabel: element.application.name,
                    },
                  ],
                });
              }

              return {
                x: label,
                y: yValue,
              };
            }),
          });
        }
      }

      // Set value
      setApplicationsUsage({
        data: processedData,
        coordinatesData: processedLabels,
      });
    } catch (error: any) {
      // Log
      console.debug(`🔴 Could not get the applications usage for entity ${props.entityId}.`, error);

      // Set error
      setErrorApplicationsUsage(error.message);
    } finally {
      // Stop loading
      setLoadingApplicationsUsage(false);
    }
  }, [props.endDate, props.entityId, props.startDate]);

  // Get statistics data
  const getStaticData = () => {
    getNumberActiveSeniors();
    getNumberActiveProfessionalCaregivers();
    getNumberActiveInformalCaregivers();
    getNumberPendingInvitations();
    getNumberActiveDigitalInclusionDevices();
    getNumberActiveQuickAccessDevices();
  };

  // Get all date-range data
  const getDynamicData = () => {
    getGeneralSystemUsage();
    getApplicationsUsage();
    getCallsEvolution();
    getNumberCalls();
    getNumberIncomingCalls();
    getNumberOutgoingCalls();
    getMostUsedApplications();
    getTopUsers();
  };

  // Get all statistics data for the entity
  useEffect(() => {
    getStaticData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.entityId]);

  // Get all date-range related data for the senior
  useEffect(() => {
    getDynamicData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.startDate, props.endDate]);

  return (
    <>
      {props.showTitle && <PageHeader title={t(translations.entity_statistics_dashboard.title)} />}
      <Box
        className={`entity-statistics-dashboard-container ${
          props.showTitle ? '' : 'entity-statistics-dashboard-container-without-title'
        }`}
      >
        {props.notShowRefreshButton === undefined && (
          <Button
            variant="outlined"
            color="primary"
            startIcon={<RefreshIcon />}
            onClick={() => {
              getStaticData();
              getDynamicData();
            }}
            disabled={
              loadingNumberActiveSeniors ||
              loadingNumberActiveProfessionalCaregivers ||
              loadingNumberActiveInformalCaregivers ||
              loadingNumberPendingInvitations ||
              loadingNumberOutgoingCalls ||
              loadingNumberIncomingCalls ||
              loadingNumberCalls ||
              loadingNumberActiveDigitalInclusionDevices ||
              loadingNumberActiveQuickAccessDevices ||
              loadingTopUsers ||
              loadingGeneralSystemUsage ||
              loadingCallsEvolution ||
              loadingMostUsedApplications ||
              loadingApplicationsUsage
            }
          >
            {t(translations.entity_statistics_dashboard.refresh_button)}
          </Button>
        )}
        <Box className="entity-statistics-dashboard-text-charts-container-1">
          <TextChart
            value={numberActiveSeniors}
            title={t(
              translations.entity_statistics_dashboard.text_charts.number_of_active_seniors_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.text_charts.number_of_active_seniors_subtitle
            )}
            loading={loadingNumberActiveSeniors}
            handleRefresh={getNumberActiveSeniors}
            error={errorNumberActiveSeniors}
          />
          <TextChart
            value={numberActiveProfessionalCaregivers}
            title={t(
              translations.entity_statistics_dashboard.text_charts
                .number_of_active_professional_caregivers_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.text_charts
                .number_of_active_professional_caregivers_subtitle
            )}
            loading={loadingNumberActiveProfessionalCaregivers}
            handleRefresh={getNumberActiveProfessionalCaregivers}
            error={errorNumberActiveProfessionalCaregivers}
          />
          <TextChart
            value={numberActiveInformalCaregivers}
            title={t(
              translations.entity_statistics_dashboard.text_charts
                .number_of_active_informal_caregivers_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.text_charts
                .number_of_active_informal_caregivers_subtitle
            )}
            loading={loadingNumberActiveInformalCaregivers}
            handleRefresh={getNumberActiveInformalCaregivers}
            error={errorNumberActiveInformalCaregivers}
          />
          <TextChart
            value={numberPendingInvitations}
            title={t(
              translations.entity_statistics_dashboard.text_charts.number_of_pending_invites_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.text_charts
                .number_of_pending_invites_subtitle
            )}
            loading={loadingNumberPendingInvitations}
            handleRefresh={getNumberPendingInvitations}
            error={errorNumberPendingInvitations}
          />
        </Box>
        <Box className="entity-statistics-dashboard-text-charts-container-2">
          <Box className="entity-statistics-dashboard-text-chart-container-2">
            <TextChart
              value={numberOutgoingCalls}
              title={t(
                translations.entity_statistics_dashboard.text_charts.number_of_outgoing_calls_title
              )}
              subtitle={t(
                translations.entity_statistics_dashboard.text_charts
                  .number_of_outgoing_calls_subtitle
              )}
              loading={loadingNumberOutgoingCalls}
              handleRefresh={getNumberOutgoingCalls}
              error={errorNumberOutgoingCalls}
            />
          </Box>
          <Box className="entity-statistics-dashboard-text-chart-container-2">
            <TextChart
              value={numberIncomingCalls}
              title={t(
                translations.entity_statistics_dashboard.text_charts.number_of_incoming_calls_title
              )}
              subtitle={t(
                translations.entity_statistics_dashboard.text_charts
                  .number_of_incoming_calls_subtitle
              )}
              loading={loadingNumberIncomingCalls}
              handleRefresh={getNumberIncomingCalls}
              error={errorNumberIncomingCalls}
            />
          </Box>
          <Box className="entity-statistics-dashboard-text-chart-container-2">
            <TextChart
              value={numberCalls}
              title={t(translations.entity_statistics_dashboard.text_charts.number_of_calls_title)}
              subtitle={t(
                translations.entity_statistics_dashboard.text_charts.number_of_calls_subtitle
              )}
              loading={loadingNumberCalls}
              handleRefresh={getNumberCalls}
              error={errorNumberCallsLast}
            />
          </Box>
        </Box>
        <Box className="entity-statistics-dashboard-table-charts-container">
          <TableChart
            id="entity-statistics-dashboard-table-chart-number-active-digital-inclusion-devices"
            rows={numberActiveDigitalInclusionDevices}
            title={t(
              translations.entity_statistics_dashboard.table_charts
                .number_of_active_digital_inclusion_devices_title
            )}
            columns={[
              {
                columnId: NumberDevicesColumnIdentifier.DeviceType,
                title: t(
                  translations.entity_statistics_dashboard.table_charts
                    .number_of_active_devices_device_type_column
                ),
                width: '50%',
                enableSort: true,
              },
              {
                columnId: NumberDevicesColumnIdentifier.Total,
                title: t(
                  translations.entity_statistics_dashboard.table_charts
                    .number_of_active_devices_total_column
                ),
                width: '50%',
                enableSort: true,
              },
            ]}
            loading={loadingNumberActiveDigitalInclusionDevices}
            handleRefresh={getNumberActiveDigitalInclusionDevices}
            error={errorNumberActiveDigitalInclusionDevices}
          />
          <TableChart
            id="entity-statistics-dashboard-table-chart-number-active-quick-access-devices"
            rows={numberActiveQuickAccessDevices}
            title={t(
              translations.entity_statistics_dashboard.table_charts
                .number_of_active_quick_access_devices_title
            )}
            columns={[
              {
                columnId: NumberDevicesColumnIdentifier.DeviceType,
                title: t(
                  translations.entity_statistics_dashboard.table_charts
                    .number_of_active_devices_device_type_column
                ),
                width: '50%',
                enableSort: true,
              },
              {
                columnId: NumberDevicesColumnIdentifier.Total,
                title: t(
                  translations.entity_statistics_dashboard.table_charts
                    .number_of_active_devices_total_column
                ),
                width: '50%',
                enableSort: true,
              },
            ]}
            loading={loadingNumberActiveQuickAccessDevices}
            handleRefresh={getNumberActiveQuickAccessDevices}
            error={errorNumberActiveQuickAccessDevices}
          />
          <TableChart
            rows={topUsers}
            title={t(translations.entity_statistics_dashboard.table_charts.top_users_title)}
            subtitle={t(translations.entity_statistics_dashboard.table_charts.top_users_subtitle)}
            columns={[
              {
                columnId: TopUsersColumnIdentifier.User,
                title: t(
                  translations.entity_statistics_dashboard.table_charts.top_users_user_column
                ),
                width: '60%',
                enableSort: true,
                avatar: {
                  type: 'user',
                  isIcon: false,
                },
              },
              {
                columnId: TopUsersColumnIdentifier.Total,
                title: t(
                  translations.entity_statistics_dashboard.table_charts
                    .top_users_total_usage_time_column
                ),
                width: '40%',
                enableSort: true,
                unitLabel: t(
                  translations.entity_statistics_dashboard.table_charts
                    .top_users_usage_time_column_unit_label
                ),
              },
            ]}
            loading={loadingTopUsers}
            handleRefresh={getTopUsers}
            error={errorTopUsers}
          />
        </Box>
        <Box className="entity-statistics-dashboard-area-charts-container">
          <AreaChart
            data={generalSystemUsage}
            title={t(
              translations.entity_statistics_dashboard.area_charts.general_system_usage_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.area_charts.general_system_usage_subtitle
            )}
            xLabel={t(
              translations.entity_statistics_dashboard.area_charts.general_system_usage_x_label
            )}
            yLabel={t(
              translations.entity_statistics_dashboard.area_charts.general_system_usage_y_label
            )}
            yUnitLabel={t(
              translations.entity_statistics_dashboard.area_charts.general_system_usage_y_unit_label
            )}
            loading={loadingGeneralSystemUsage}
            handleRefresh={() => getGeneralSystemUsage()}
            yDomain={[0, Math.max(...generalSystemUsage.map((obj) => obj.y)) + 5]}
            error={errorGeneralSystemUsage}
          />
        </Box>
        <Box className="entity-statistics-dashboard-bar-charts-container">
          <BarChart
            data={callsEvolution}
            title={t(translations.entity_statistics_dashboard.bar_charts.calls_evolution_title)}
            subtitle={t(
              translations.entity_statistics_dashboard.bar_charts.calls_evolution_subtitle
            )}
            xLabel={t(translations.entity_statistics_dashboard.bar_charts.calls_evolution_x_label)}
            yLabel={t(translations.entity_statistics_dashboard.bar_charts.calls_evolution_y_label)}
            loading={loadingCallsEvolution}
            handleRefresh={() => getCallsEvolution()}
            yDomain={[0, Math.max(...callsEvolution.map((obj) => obj.y)) + 1]}
            error={errorCallsEvolution}
          />
        </Box>
        <Box className="entity-statistics-dashboard-pie-charts-container">
          <PieChart
            data={mostUsedApplications}
            title={t(
              translations.entity_statistics_dashboard.pie_charts.most_used_applications_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.pie_charts.most_used_applications_subtitle
            )}
            xLabel={t(
              translations.entity_statistics_dashboard.pie_charts.most_used_applications_x_label
            )}
            yLabel={t(
              translations.entity_statistics_dashboard.pie_charts.most_used_applications_y_label
            )}
            yUnitLabel={t(
              translations.entity_statistics_dashboard.pie_charts
                .most_used_applications_y_unit_label
            )}
            loading={loadingMostUsedApplications}
            handleRefresh={getMostUsedApplications}
            error={errorMostUsedApplications}
          />
        </Box>
        <Box className="entity-statistics-dashboard-stack-charts-container">
          <StackChart
            data={applicationsUsage}
            title={t(
              translations.entity_statistics_dashboard.stack_charts.applications_usage_title
            )}
            subtitle={t(
              translations.entity_statistics_dashboard.stack_charts.applications_usage_subtitle
            )}
            xLabel={t(
              translations.entity_statistics_dashboard.stack_charts.applications_usage_x_label
            )}
            yLabel={t(
              translations.entity_statistics_dashboard.stack_charts.applications_usage_y_label
            )}
            yUnitLabel={t(
              translations.entity_statistics_dashboard.stack_charts.applications_usage_y_unit_label
            )}
            stackLabel={{
              singular: t(
                translations.entity_statistics_dashboard.stack_charts
                  .applications_usage_singular_stack_label
              ),
              plural: t(
                translations.entity_statistics_dashboard.stack_charts
                  .applications_usage_plural_stack_label
              ),
            }}
            loading={loadingApplicationsUsage}
            handleRefresh={() => getApplicationsUsage()}
            error={errorApplicationsUsage}
          />
        </Box>
      </Box>
    </>
  );
};
